import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Inbox, MailPlus, Bot, CheckCircle2, X, Mail, AlertTriangle, MessageSquare, ShieldAlert, Sparkles, Search, MailQuestion, Reply, ArrowDownUp } from "lucide-react";
import { ReservaForm, reservaToForm, formToPayload } from "@/components/reservas/ReservaForm";
import IngestEmailDialog from "@/components/reservas/IngestEmailDialog";
import AprobacionesTab from "@/components/bandeja/AprobacionesTab";
import RespuestasTab from "@/components/bandeja/RespuestasTab";
import SinReservaTab from "@/components/bandeja/SinReservaTab";
import LimpiezaBandejaDialog from "@/components/bandeja/LimpiezaBandejaDialog";
import CorreosEquipoDialog from "@/components/bandeja/CorreosEquipoDialog";
import MasivoBoton, { MasivoDialog } from "@/components/bandeja/MasivoDialog";
import ResponderAccionDialog from "@/components/bandeja/ResponderAccionDialog";
import AccionCard from "@/components/bandeja/AccionCard";
import UrgenciaBadge from "@/components/bandeja/UrgenciaBadge";
import { ordenarPorUrgencia } from "@/lib/urgencia";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { paxResumen, idiomaAbbr } from "@/lib/constants";
import { formatFechaCorta, formatHoraIngreso } from "@/lib/agenda";
import { nombreTourDisplay } from "@/lib/tourDisplay";

function IdiomaBadge({ idioma }) {
  const abr = idiomaAbbr(idioma);
  return (
    <span
      className={`inline-flex items-center justify-center px-1 py-0 rounded text-[9px] font-bold border leading-4 ${
        abr === "EN" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-600 border-slate-200"
      }`}
      data-testid={`bandeja-idioma-${abr.toLowerCase()}`}
    >
      {abr}
    </span>
  );
}

function RawEmailPanel({ raw }) {
  if (!raw) {
    return (
      <Card className="p-4 text-sm text-slate-400 h-full flex items-center justify-center">
        Sin correo original asociado (carga manual o histórica).
      </Card>
    );
  }
  return (
    <Card className="p-0 overflow-hidden h-full flex flex-col" data-testid="bandeja-raw-email">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Mail className="h-4 w-4" />
          <span className="font-medium text-slate-700">Correo original</span>
        </div>
        <p className="text-sm font-medium text-slate-900 mt-1">{raw.asunto || "(sin asunto)"}</p>
        <p className="text-[11px] text-slate-400">{raw.remitente} · {raw.buzon_email || "—"}</p>
      </div>
      <pre className="p-4 text-[12px] leading-relaxed text-slate-700 whitespace-pre-wrap font-sans overflow-y-auto max-h-[420px]">
        {raw.cuerpo}
      </pre>
    </Card>
  );
}

function RevisionPanel({ reserva, tours, agencias, onResolved }) {
  const [form, setForm] = useState(reservaToForm(reserva));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(reservaToForm(reserva));
  }, [reserva]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const confirmar = async () => {
    if (!form.tour_id) return toast.error("Asigná un tour del catálogo.");
    if (!form.fecha || !form.hora) return toast.error("Completá fecha y hora.");
    if (!form.nombre.trim()) return toast.error("Indicá el nombre del pasajero.");
    setSaving(true);
    try {
      const payload = formToPayload(form);
      const { data } = await api.post(`/reservas/${reserva.id}/confirmar`, payload);
      toast.success("Reserva confirmada y cargada a la agenda.");
      // PROMPT 116: el sistema aprendio el nombre del producto para este tour.
      const aprendido = data?.tour_aprendido;
      if (aprendido) {
        const otras = aprendido.otras_reservas
          ? ` Otras ${aprendido.otras_reservas} reservas de la Bandeja ya quedaron con ese tour.`
          : "";
        toast.info(`Aprendido: la próxima vez «${aprendido.texto}» entra como ${aprendido.tour}.${otras}`, { duration: 10000 });
      }
      onResolved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const descartar = async () => {
    setSaving(true);
    try {
      await api.post(`/reservas/${reserva.id}/estado`, { nuevo_estado: "rechazada", motivo: "Descartada en bandeja" });
      toast.success("Reserva descartada.");
      onResolved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4 h-full flex flex-col" data-testid="bandeja-revision-panel">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <span className="text-sm font-medium text-slate-800">Confirmá o ajustá los datos</span>
        <span className="ml-auto text-[11px] text-slate-400">Confianza {Math.round((reserva.confianza_parsing || 0) * 100)}%</span>
      </div>
      {reserva.motivo_revision && (
        <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-3">
          {reserva.motivo_revision}
        </p>
      )}
      {reserva.requiere_asignacion_agencia && (
        <p className="text-[12px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1 mb-3" data-testid="bandeja-asignar-agencia-hint">
          No se reconoció la agencia de origen. Asignala abajo: se recordará el remitente para la próxima.
        </p>
      )}
      <div className="flex-1 overflow-y-auto pr-1">
        <ReservaForm form={form} set={set} tours={tours} agencias={agencias} showCodigo />
      </div>
      <div className="flex gap-2 justify-end pt-3 border-t border-slate-100 mt-3">
        <Button variant="outline" onClick={descartar} disabled={saving} data-testid="bandeja-descartar-btn" className="gap-2">
          <X className="h-4 w-4" /> Descartar
        </Button>
        <Button onClick={confirmar} disabled={saving} data-testid="bandeja-confirmar-btn" className="gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirmar
        </Button>
      </div>
    </Card>
  );
}

// PROMPT 109: aviso cuando una lista muestra menos de lo que hay. Las listas tienen techo
// para no cargar megas de correos de golpe, pero ya no cortan en silencio.
// PROMPT 141: `porUrgencia` = la lista vino ordenada por fecha del tour (Acciones).
function AvisoTope({ mostradas, total, buscando, porUrgencia }) {
  if (!total || total <= mostradas) return null;
  return (
    <p
      className="mb-3 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2"
      data-testid="aviso-tope"
    >
      Se muestran {mostradas.toLocaleString("es")} de {total.toLocaleString("es")}.{" "}
      {buscando
        ? "Escribí algo más específico para achicar la búsqueda."
        : porUrgencia
        ? "Son las de tour más cercano; para encontrar una en particular, usá el buscador."
        : "Son las que entraron más recientemente; para encontrar una en particular, usá el buscador."}
    </p>
  );
}

// PROMPT 109: el total real de una lista con techo (cabecera X-Total-Count), o null.
function totalDe(res) {
  const t = parseInt(res?.headers?.["x-total-count"], 10);
  return Number.isFinite(t) ? t : null;
}

export default function BandejaPage() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [acciones, setAcciones] = useState([]);
  const [tours, setTours] = useState([]);
  const [agencias, setAgencias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selId, setSelId] = useState(null);
  const [ingestOpen, setIngestOpen] = useState(false);
  const [limpiezaOpen, setLimpiezaOpen] = useState(false);
  const [quitarOpen, setQuitarOpen] = useState(false); // PROMPT L32
  const [equipoOpen, setEquipoOpen] = useState(false); // PROMPT 139: correos que mando el equipo
  const [responderTarget, setResponderTarget] = useState(null);
  const [aprobCount, setAprobCount] = useState(0);
  const [respCount, setRespCount] = useState(0);
  const [sinReservaCount, setSinReservaCount] = useState(0);
  const [search, setSearch] = useState("");
  // PROMPT 108: por defecto se ven las reservas con el tour de hoy en adelante (o sin fecha);
  // las de fechas que ya pasaron, con el boton. `encontradas` es lo que devuelve el buscador
  // cuando busca en TODA la Bandeja (null = no se esta buscando en el servidor).
  const [verPasadas, setVerPasadas] = useState(false);
  const [encontradas, setEncontradas] = useState(null);
  // PROMPT 109: los totales reales de cada lista, y lo que el buscador encontro en Acciones.
  const [totalRevisar, setTotalRevisar] = useState(null);
  const [totalAcciones, setTotalAcciones] = useState(null);
  const [totalEncontradas, setTotalEncontradas] = useState(null);
  const [accionesEncontradas, setAccionesEncontradas] = useState(null);
  const [totalAccionesEncontradas, setTotalAccionesEncontradas] = useState(null);
  // PROMPT 141: por defecto, lo mas urgente primero segun la FECHA DEL TOUR (no la de llegada).
  const [orden, setOrden] = useState("urgencia");
  const porUrgencia = orden === "urgencia";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Cada llamada con timeout propio: si una tarda demasiado (ej. Bandeja muy
      // cargada en producción) no debe trabar TODA la pantalla con un spinner
      // infinito -- se usa allSettled para que las que sí respondan se muestren
      // igual, y solo la principal (bandeja) corta la carga si falla.
      const [b, a, ap, re] = await Promise.allSettled([
        api.get("/reservas/bandeja", { params: { pasadas: verPasadas }, timeout: 20000 }),
        api.get("/captura/acciones", { params: porUrgencia ? { orden: "urgencia" } : {}, timeout: 20000 }),
        api.get("/cancelaciones/aprobaciones", { timeout: 20000 }),
        api.get("/respuestas", { params: { estado: "borrador" }, timeout: 20000 }),
      ]);
      if (b.status === "fulfilled") {
        setItems(b.value.data);
        setTotalRevisar(totalDe(b.value)); // PROMPT 109
        const primera = (porUrgencia ? ordenarPorUrgencia(b.value.data, (r) => r.fecha) : b.value.data)[0];
        setSelId((prev) => (b.value.data.find((r) => r.id === prev) ? prev : primera?.id || null));
      } else {
        setItems([]);
        toast.error(formatApiError(b.reason?.response?.data?.detail) || "No se pudo cargar 'A revisar'. Reintentá.");
      }
      setAcciones(a.status === "fulfilled" ? a.value.data : []);
      setTotalAcciones(a.status === "fulfilled" ? totalDe(a.value) : null); // PROMPT 109
      setAprobCount(ap.status === "fulfilled" ? ap.value.data.length : 0);
      setRespCount(re.status === "fulfilled" ? re.value.data.length : 0);
      const fallidas = [a, ap, re].filter((r) => r.status === "rejected").length;
      if (fallidas > 0 && b.status === "fulfilled") {
        toast.warning(`${fallidas} pestaña(s) de la Bandeja no cargaron a tiempo. "A revisar" sí está al día.`);
      }
    } finally {
      setLoading(false);
    }
  }, [verPasadas, porUrgencia]);

  useEffect(() => {
    api.get("/tours", { params: { activo: true } }).then(({ data }) => setTours(data)).catch(() => {});
    api.get("/agencias").then(({ data }) => setAgencias(data)).catch(() => {});
    api.get("/marcas").then(({ data }) => setMarcas(data)).catch(() => {});
    load();
  }, [load]);

  const resolverAccion = async (id) => {
    try {
      await api.post(`/captura/acciones/${id}/resolver`);
      toast.success("Acción marcada como resuelta.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // PROMPT 108: con 4 caracteres o mas, el buscador pregunta al servidor y mira TODA la
  // Bandeja, pasadas incluidas. Filtrar solo lo cargado en pantalla dejaba afuera, para
  // siempre, lo que no entraba en la lista. Si encuentra algo, lo abre.
  useEffect(() => {
    const t = search.trim();
    if (t.length < 4) {
      setEncontradas(null);
      setTotalEncontradas(null);
      setAccionesEncontradas(null);
      setTotalAccionesEncontradas(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      api.get("/reservas/bandeja", { params: { q: t }, timeout: 20000 })
        .then((res) => {
          const data = res.data;
          setEncontradas(data);
          setTotalEncontradas(totalDe(res));
          const primera = (porUrgencia ? ordenarPorUrgencia(data, (r) => r.fecha) : data)[0];
          if (primera) setSelId((prev) => (data.find((r) => r.id === prev) ? prev : primera.id));
        })
        .catch(() => { setEncontradas(null); setTotalEncontradas(null); });
      // PROMPT 109: Acciones tambien se busca en el servidor. Filtrar solo los 500 cargados
      // dejaba afuera, para siempre, a los correos de clientes mas viejos.
      api.get("/captura/acciones", { params: porUrgencia ? { q: t, orden: "urgencia" } : { q: t }, timeout: 20000 })
        .then((res) => { setAccionesEncontradas(res.data); setTotalAccionesEncontradas(totalDe(res)); })
        .catch(() => { setAccionesEncontradas(null); setTotalAccionesEncontradas(null); });
    }, 400);
    return () => clearTimeout(timer);
  }, [search, items, acciones, porUrgencia]);

  const term = search.trim().toLowerCase();
  const itemsSinOrden = encontradas !== null
    ? encontradas
    : !term
    ? items
    : items.filter((r) => {
        const blob = [
          r.codigo_booking, r.pasajero?.nombre, r.pasajero?.apellido, r.pasajero?.email,
        ].filter(Boolean).join(" ").toLowerCase();
        return blob.includes(term);
      });
  // PROMPT 141: Acciones ya viene ordenada del servidor; "A revisar" se ordena aca (viene completa).
  const itemsFiltrados = porUrgencia ? ordenarPorUrgencia(itemsSinOrden, (r) => r.fecha) : itemsSinOrden;
  const seleccionada = itemsFiltrados.find((r) => r.id === selId) || items.find((r) => r.id === selId);
  const accionesFiltradas = accionesEncontradas !== null
    ? accionesEncontradas
    : !term
    ? acciones
    : acciones.filter((a) => {
        const blob = [
          a.parsed?.codigo_booking, a.asunto, a.remitente, a.parsed?.mensaje_texto,
        ].filter(Boolean).join(" ").toLowerCase();
        return blob.includes(term);
      });

  return (
    <div className="space-y-4" data-testid="bandeja-page">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <p className="text-sm text-slate-500">
          Reservas con baja confianza y correos que requieren tu confirmación manual.
        </p>
        <div className="flex gap-2 flex-wrap">
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar localizador, nombre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="bandeja-search-input"
            />
          </div>
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setLimpiezaOpen(true)}
              data-testid="bandeja-limpiar-btn"
              className="gap-2 border-violet-200 text-violet-700 hover:bg-violet-50"
            >
              <Sparkles className="h-4 w-4" /> Limpiar Bandeja
            </Button>
          )}
          {/* PROMPT L32: saca de A revisar solo lo que no es reserva (no confirma ni cancela reservas) */}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setQuitarOpen(true)}
              data-testid="bandeja-quitar-no-reservas-btn"
              className="gap-2"
            >
              <X className="h-4 w-4" /> Quitar lo que no es reserva
            </Button>
          )}
          {/* PROMPT 139: lo que entro por respuestas y envios del propio equipo */}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setEquipoOpen(true)}
              data-testid="bandeja-correos-equipo-btn"
              title="Reservas y pedidos que entraron por correos que mandó el propio equipo (respuestas a agencias o clientes)."
              className="gap-2"
            >
              <Reply className="h-4 w-4" /> Correos del equipo
            </Button>
          )}
          {/* PROMPT 108: las de tours que ya pasaron, aparte, para que no tapen a las que se pueden resolver */}
          <Button
            variant={verPasadas ? "default" : "outline"}
            onClick={() => setVerPasadas((v) => !v)}
            data-testid="bandeja-ver-pasadas"
            title="La Bandeja muestra las reservas con el tour de hoy en adelante, o sin fecha. Con este botón se ven las de fechas que ya pasaron."
            className="gap-2"
          >
            {verPasadas ? "Ver de hoy en adelante" : "Ver fechas pasadas"}
          </Button>
          {/* PROMPT 141: orden por la fecha del tour (lo mas urgente arriba) o por llegada */}
          <Button
            variant="outline"
            onClick={() => setOrden((o) => (o === "urgencia" ? "llegada" : "urgencia"))}
            data-testid="bandeja-orden"
            title="Cambia el orden de 'A revisar' y 'Acciones': por la fecha del tour que dice el correo (lo más urgente arriba) o por cuándo llegó."
            className="gap-2"
          >
            <ArrowDownUp className="h-4 w-4" />
            {porUrgencia ? "Orden: más urgentes primero" : "Orden: últimos en llegar"}
          </Button>
          <Button variant="outline" onClick={() => setIngestOpen(true)} data-testid="bandeja-paste-email" className="gap-2">
            <MailPlus className="h-4 w-4" /> Pegar correo
          </Button>
        </div>
      </div>

      <Tabs defaultValue="revisar">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="revisar" data-testid="tab-revisar" className="gap-2">
            <Inbox className="h-4 w-4" /> A revisar
            {/* PROMPT 109: el numero es el total real, no lo que entro en la lista */}
            <span className="ml-1 text-[11px] bg-white rounded px-1.5">
              {((encontradas !== null ? totalEncontradas : term ? null : totalRevisar) ?? itemsFiltrados.length).toLocaleString("es")}
            </span>
          </TabsTrigger>
          <TabsTrigger value="acciones" data-testid="tab-acciones" className="gap-2">
            <MessageSquare className="h-4 w-4" /> Acciones
            <span className="ml-1 text-[11px] bg-white rounded px-1.5">
              {((accionesEncontradas !== null ? totalAccionesEncontradas : term ? null : totalAcciones) ?? accionesFiltradas.length).toLocaleString("es")}
            </span>
          </TabsTrigger>
          <TabsTrigger value="aprobaciones" data-testid="tab-aprobaciones" className="gap-2">
            <ShieldAlert className="h-4 w-4" /> Aprobaciones
            <span className="ml-1 text-[11px] bg-white rounded px-1.5">{aprobCount}</span>
          </TabsTrigger>
          <TabsTrigger value="respuestas" data-testid="tab-respuestas" className="gap-2">
            <Mail className="h-4 w-4" /> Respuestas
            <span className="ml-1 text-[11px] bg-white rounded px-1.5">{respCount}</span>
          </TabsTrigger>
          <TabsTrigger value="sin-reserva" data-testid="tab-sin-reserva" className="gap-2">
            <MailQuestion className="h-4 w-4" /> Correos sin reserva
            <span className="ml-1 text-[11px] bg-white rounded px-1.5">{sinReservaCount}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="revisar" className="mt-4">
          <AvisoTope
            mostradas={itemsFiltrados.length}
            total={encontradas !== null ? totalEncontradas : term ? null : totalRevisar}
            buscando={encontradas !== null}
          />
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : itemsFiltrados.length === 0 ? (
            <Card className="p-10 text-center text-slate-400 text-sm" data-testid="bandeja-empty">
              <Inbox className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              {term ? "Ninguna reserva de la Bandeja coincide con la búsqueda." : "No hay reservas pendientes de revisión. ¡Todo al día!"}
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="space-y-2 lg:max-h-[600px] lg:overflow-y-auto">
                {itemsFiltrados.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setSelId(r.id)}
                    data-testid={`bandeja-item-${r.id}`}
                    className={`w-full text-left rounded-md border p-3 transition-colors ${
                      r.id === selId ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[11px] text-slate-500 truncate">{r.codigo_booking}</span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {/* PROMPT 141: urgencia por la fecha del tour */}
                        <UrgenciaBadge fecha={r.fecha} testid={`bandeja-urgencia-${r.id}`} />
                        <span className="text-[10px] text-slate-400">{Math.round((r.confianza_parsing || 0) * 100)}%</span>
                      </span>
                    </div>
                    <p className="text-sm font-medium text-slate-900 mt-1 flex items-center gap-1.5">
                      {r.pasajero?.nombre} {r.pasajero?.apellido || ""}
                      <IdiomaBadge idioma={r.pasajero?.idioma} />
                    </p>
                    <p className="text-[12px] text-slate-500 truncate">{r.tour_nombre ? nombreTourDisplay(r.tour_nombre, r.modalidad) : (r.producto_raw || "Sin tour")}</p>
                    <p className="text-[11px] text-slate-400">
                      {formatFechaCorta(r.fecha)} · {paxResumen(r)}
                      <span data-testid={`bandeja-item-ingreso-${r.id}`}> · Ingresó {formatHoraIngreso(r.created_at)}</span>
                    </p>
                  </button>
                ))}
              </div>
              <div className="lg:col-span-1">
                <RawEmailPanel raw={seleccionada?.raw_email} />
              </div>
              <div className="lg:col-span-1">
                {seleccionada && (
                  <RevisionPanel reserva={seleccionada} tours={tours} agencias={agencias} onResolved={load} />
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="acciones" className="mt-4">
          {isAdmin && (
            <div className="mb-3">
              <MasivoBoton modo="acciones" busqueda={search} refresco={acciones.length} onDone={load} />
            </div>
          )}
          <AvisoTope
            mostradas={accionesFiltradas.length}
            total={accionesEncontradas !== null ? totalAccionesEncontradas : term ? null : totalAcciones}
            buscando={accionesEncontradas !== null}
            porUrgencia={porUrgencia}
          />
          <p className="text-[12px] text-slate-500 mb-3 bg-slate-50 border border-slate-200 rounded px-3 py-2">
            Correos que necesitan una respuesta o acción manual (mensajes de clientes, cancelaciones sin datos, cambios,
            GYG Care). Usá <strong>"Ver correo completo"</strong> para leer el mensaje entero (no solo el resumen) y{" "}
            <strong>"Traducir"</strong> para leerlo en español sin salir de la app -- no hace falta el traductor del
            navegador. Con <strong>"Responder"</strong> el correo sale directo desde la app, desde la casilla de la
            marca correspondiente, y el ítem se resuelve solo. Usá <strong>"Resolver"</strong> solo para el caso raro
            (ya lo resolviste por teléfono, o no hace falta responder) — Resolver NO envía nada al cliente.
          </p>
          {accionesFiltradas.length === 0 ? (
            <Card className="p-10 text-center text-slate-400 text-sm">
              <MessageSquare className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              {term ? "Ningún correo de Acciones coincide con la búsqueda." : "No hay correos vinculados pendientes (cambios, mensajes o GYG Care)."}
            </Card>
          ) : (
            <div className="space-y-2">
              {accionesFiltradas.map((a) => (
                <AccionCard key={a.id} a={a} onResponder={setResponderTarget} onResolver={resolverAccion} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="aprobaciones" className="mt-4">
          <AprobacionesTab onCount={setAprobCount} onChanged={load} />
        </TabsContent>

        <TabsContent value="respuestas" className="mt-4">
          <RespuestasTab onCount={setRespCount} onChanged={load} />
        </TabsContent>

        <TabsContent value="sin-reserva" className="mt-4">
          <SinReservaTab onCount={setSinReservaCount} />
        </TabsContent>
      </Tabs>

      <IngestEmailDialog open={ingestOpen} onOpenChange={setIngestOpen} onProcessed={load} />
      {isAdmin && (
        <LimpiezaBandejaDialog open={limpiezaOpen} onOpenChange={setLimpiezaOpen} onDone={load} />
      )}
      {isAdmin && (
        <MasivoDialog open={quitarOpen} onOpenChange={setQuitarOpen} modo="revisar" onDone={load} />
      )}
      {isAdmin && (
        <CorreosEquipoDialog open={equipoOpen} onOpenChange={setEquipoOpen} onDone={load} />
      )}
      <ResponderAccionDialog
        open={!!responderTarget}
        onOpenChange={(v) => !v && setResponderTarget(null)}
        accion={responderTarget}
        marcas={marcas}
        onSent={load}
      />
    </div>
  );
}
